import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

function Memory() {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const pdfInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({
    title: '',
    content: '',
    window_number: '',
    date_range: ''
  });

  useEffect(() => { fetchMemories(); }, []);

  const fetchMemories = async () => {
    const { data, error } = await supabase
      .from('memories')
      .select('*')
      .order('window_number', { ascending: true });
    if (error) {
      console.error('Failed to load memories:', error);
    } else {
      setMemories(data || []);
    }
    setLoading(false);
  };

  const addMemory = async () => {
    if (form.title.trim() === '' || form.content.trim() === '') return;
    const { data, error } = await supabase
      .from('memories')
      .insert([{
        title: form.title,
        content: form.content,
        window_number: form.window_number ? parseInt(form.window_number) : null,
        date_range: form.date_range || null
      }])
      .select();
    if (error) {
      console.error('Failed to add:', error);
    } else if (data) {
      setMemories(prev => [...prev, data[0]].sort((a, b) => (a.window_number || 0) - (b.window_number || 0)));
      setForm({ title: '', content: '', window_number: '', date_range: '' });
      setShowForm(false);
    }
  };

  const deleteMemory = async (id, e) => {
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    var id = deleteConfirm;
    setDeleteConfirm(null);
    await supabase.from('memories').delete().eq('id', id);
    setMemories(prev => prev.filter(m => m.id !== id));
    if (expanded === id) setExpanded(null);
  };

  const handlePdfImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setImportMsg('\u53EA\u652F\u6301 PDF \u6587\u4EF6');
      return;
    }

    setImporting(true);
    setImportMsg('\u6B63\u5728\u89E3\u6790\u5E76\u63D0\u53D6\u8BB0\u5FC6...');

    try {
      const formData = new FormData();
      formData.append('pdf', file);

      const resp = await fetch('/api/memories/import-pdf', {
        method: 'POST',
        body: formData
      });
      const data = await resp.json();

      if (data.ok) {
        setImportMsg(data.message);
        await fetchMemories();
      } else {
        setImportMsg('\u5BFC\u5165\u5931\u8D25: ' + (data.error || '\u672A\u77E5\u9519\u8BEF'));
      }
    } catch (err) {
      setImportMsg('\u5BFC\u5165\u5931\u8D25: ' + err.message);
    }

    setImporting(false);
    if (pdfInputRef.current) pdfInputRef.current.value = '';
    setTimeout(() => setImportMsg(''), 5000);
  };

  return (
    <div className="memory">
      <div className="memory-header">
        <div className="memory-title">{'\u8BB0\u5FC6\u5E93'}</div>
        <div className="memory-header-btns">
          <button
            className="memory-import-btn"
            onClick={() => pdfInputRef.current && pdfInputRef.current.click()}
            disabled={importing}
          >
            {importing ? '...' : 'PDF \u5BFC\u5165'}
          </button>
          <button className="memory-add-btn" onClick={() => setShowForm(!showForm)}>
            {showForm ? '\u53D6\u6D88' : '+ \u6DFB\u52A0\u8BB0\u5FC6'}
          </button>
        </div>
        <input
          type="file"
          accept=".pdf"
          ref={pdfInputRef}
          style={{ display: 'none' }}
          onChange={handlePdfImport}
        />
      </div>

      {importMsg && (
        <div className="memory-import-msg">{importMsg}</div>
      )}

      {showForm && (
        <div className="memory-form">
          <input
            className="memory-input"
            placeholder={'\u6807\u9898\uFF08\u5982\uFF1A\u7A97\u53E3\u8BB0\u5FC6 #001\uFF09'}
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
          />
          <div className="memory-form-row">
            <input
              className="memory-input-small"
              placeholder={'\u7A97\u53E3\u7F16\u53F7'}
              type="number"
              value={form.window_number}
              onChange={(e) => setForm({...form, window_number: e.target.value})}
            />
            <input
              className="memory-input-small"
              placeholder={'\u65E5\u671F\u8303\u56F4'}
              value={form.date_range}
              onChange={(e) => setForm({...form, date_range: e.target.value})}
            />
          </div>
          <textarea
            className="memory-textarea"
            placeholder={'\u8BB0\u5FC6\u5185\u5BB9...'}
            value={form.content}
            onChange={(e) => setForm({...form, content: e.target.value})}
            rows={8}
          />
          <button className="memory-submit-btn" onClick={addMemory}>{'\u4FDD\u5B58'}</button>
        </div>
      )}

      <div className="memory-list">
        {loading ? (
          <div className="memory-empty">{'\u52A0\u8F7D\u4E2D...'}</div>
        ) : memories.length === 0 ? (
          <div className="memory-empty">{'\u8FD8\u6CA1\u6709\u8BB0\u5FC6'}</div>
        ) : (
          memories.map((mem) => (
            <div
              key={mem.id}
              className={'memory-card' + (expanded === mem.id ? ' expanded' : '')}
              onClick={() => setExpanded(expanded === mem.id ? null : mem.id)}
            >
              <div className="memory-card-header">
                <div className="memory-card-meta">
                  {mem.window_number && (
                    <div className="memory-card-number">#{mem.window_number.toString().padStart(3, '0')}</div>
                  )}
                  {mem.date_range && (
                    <div className="memory-card-date">{mem.date_range}</div>
                  )}
                </div>
                <button className="memory-card-delete" onClick={(e) => deleteMemory(mem.id, e)}>{'\u00D7'}</button>
              </div>
              <div className="memory-card-title">{mem.title}</div>
              {expanded === mem.id ? (
                <div className="memory-card-content">{mem.content}</div>
              ) : (
                <div className="memory-card-preview">
                  {mem.content.length > 80 ? mem.content.substring(0, 80) + '...' : mem.content}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {deleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'flex',
          alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setDeleteConfirm(null)}>
          <div style={{
            background: 'var(--card)', borderRadius: 16, padding: '24px 20px',
            width: '80%', maxWidth: 320, textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              确认删除
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.6 }}>
              删除后无法恢复，确定要删除这条记忆吗？
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid var(--border)',
                  background: 'var(--bg2)', color: 'var(--text2)', fontSize: 14, cursor: 'pointer',
                  fontFamily: 'var(--font-sans)'
                }}
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                  background: '#e74c3c', color: '#fff', fontSize: 14, cursor: 'pointer',
                  fontFamily: 'var(--font-sans)'
                }}
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Memory;
